// INV-CF1: copy of a structural selection contains every selected top-level block.
// INV-CF4: invalid carrier falls back to the next supported clipboard representation.
import { ulid } from '@w/utils'
import { pageDocument } from '../doc-blocks'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { blocksToMd } from '@w/content-blocks/src/serializer'
import { unsavedCopy, questionIds } from './unsaved-copy'
import { pasteBlocks } from './paste'
import { blocksToHtml } from './html'
import type { EditorIntent } from './intents'
import { lexicalClipboardBlocks } from './lexical-clipboard'
import { safeLinkHref, selectedTableRows } from './rich-content'
import type { CoreState } from './state'
import { children, gapOf, locate, plain, rows, sliceInline } from './tree-read'

/**
 * The pasted `fragment` as `page` (the page document before the paste) can hold it: a pasted comment ref keeps its thread only when the
 * page's registry has that thread and no ref to it is in the page or earlier in the fragment; otherwise it is plain
 * text. A pasted registry is dropped. A page names each thread once and only threads its registry holds (content-blocks
 * comments.ts validateCommentDocument), so a copy pasted beside its source, or into another page, stays a valid
 * document; a cut pasted back keeps its anchor, as its source ref is gone (a paste typed over its own source ref reads
 * the page before the edit and pastes plain text). Every nested Block array is visited, question
 * option lists (`Blocks[]`) included.
 */
export function pastedComments(fragment: Blocks, page: unknown): Blocks {
  const threads = new Set<string>()
  const refs = new Set<string>()
  const collect = (value: unknown): void => {
    if (!value || typeof value !== 'object') return
    if (record(value) && value.type === 'comments' && Array.isArray(value.threads)) {
      for (const thread of value.threads) if (record(thread) && typeof thread.threadId === 'string') threads.add(thread.threadId)
    }
    if (record(value) && value.type === 'comment' && typeof value.commentId === 'string') refs.add(value.commentId)
    for (const child of Object.values(value)) collect(child)
  }
  // The page's threads and references matter only to a pasted comment: a fragment without one never walks the page.
  let collected = false
  const isBlock = (value: unknown): value is Block => record(value) && typeof value.type === 'string'
  const visit = (value: unknown): unknown => {
    if (!Array.isArray(value)) return value
    return value.flatMap((item: unknown): unknown[] => {
      if (!isBlock(item)) return [visit(item)]
      if (item.type === 'comments') return []
      if (item.type === 'comment') {
        if (!collected) {
          collected = true
          collect(page)
        }
        if (threads.has(item.commentId) && !refs.has(item.commentId)) {
          refs.add(item.commentId)
          return [{ ...item, children: visit(item.children) }]
        }
        return visit(item.children) as unknown[]
      }
      return [Object.fromEntries(Object.entries(item).map(([key, field]: [string, unknown]) => [key, visit(field)]))]
    })
  }
  return visit(fragment) as Blocks
}

/**
 * Clipboard flavours a view reads and writes: `text` is Markdown, `html` text/html (a Blocks carrier span marks our own
 * copies), `lexical` the Lexical view's clipboard JSON, `files` pasted files.
 */
export interface ClipboardData { text?: string; html?: string; lexical?: string; files?: readonly File[] }

/** Clipboard slices preserve inline formats and block boundaries without changing state. */
export function selectedMarkdown(state: CoreState): string {
  return markdownOf(selectedFragment(state))
}

/** A selected fragment's Markdown; a text range's edge spaces are escaped so they survive a Markdown paste. */
function markdownOf({ fragment, range }: { fragment: Blocks; range: boolean }): string {
  if (fragment.length === 0) return ''
  const md = blocksToMd(fragment)
  return range ? md.replaceAll(/^ +| +$/g, (spaces) => '&#32;'.repeat(spaces.length)) : md
}

/**
 * The selection as a Blocks fragment: its table cells, its nodes, the blocks between two document gaps, or its text
 * range's row slices (`range`) inside their pruned ancestors; empty without a selection or for a caret.
 */
export function selectedFragment(state: CoreState): { fragment: Blocks; range: boolean } {
  const fragment = (blocks: Blocks) => ({ fragment: blocks, range: false })
  if (!state.selection) return fragment([])
  if (state.selection.table) {
    const selected = selectedTableRows(state)
    const tableRows: Blocks = []
    for (const entry of selected) {
      const parent = entry.parents.find((block) => block.type === 'tr')
      if (!parent) continue
      let row = tableRows.find((block) => block.id === parent.id)
      if (!row) { row = { id: parent.id, type: 'tr', children: [] }; tableRows.push(row) }
      children(row).push(structuredClone(entry.block))
    }
    return fragment([{ id: state.selection.table.tableId, type: 'table', children: tableRows } as Block])
  }
  if (state.selection.nodes?.length) return fragment(selectedNodes(state.blocks, state.selection.nodes))
  const anchorGap = gapOf(state.blocks, state.selection.anchor)
  const focusGap = gapOf(state.blocks, state.selection.focus)
  if (anchorGap?.siblings === state.blocks && focusGap?.siblings === state.blocks) {
    return fragment(state.blocks.slice(
      Math.min(anchorGap.index, focusGap.index), Math.max(anchorGap.index, focusGap.index)))
  }
  const entries = rows(state.blocks)
  const points = [locate(entries, state.selection.anchor), locate(entries, state.selection.focus)]
    .toSorted((a, b) => a.row - b.row || a.offset - b.offset)
  const [start, end] = points
  if (start.row === end.row && start.offset === end.offset && !anchorGap && !focusGap) return fragment([])
  const selected = new Map(entries.slice(start.row, end.row + 1).map((entry, index) => [
    entry.block.id, { ...entry.block, children: sliceInline(entry.block.children,
      index === 0 ? start.offset : 0,
      start.row + index === end.row ? end.offset : plain(entry.block).length),
    } as Block,
  ]))
  const rootGaps = [anchorGap, focusGap].flatMap(gap => gap?.siblings === state.blocks ? [gap] : [])
  const first = Math.min(state.blocks.indexOf(entries[start.row].parents[0] ?? entries[start.row].block),
    ...rootGaps.map(gap => gap.index - 0.5))
  const last = Math.max(state.blocks.indexOf(entries[end.row].parents[0] ?? entries[end.row].block),
    ...rootGaps.map(gap => gap.index - 0.5))
  // A top-level block strictly inside the range without selected rows (divider, media, question) is copied whole.
  const prune = (items: Blocks): Blocks => items.flatMap((block): Blocks => {
    const row = selected.get(block.id)
    if (row) return [row]
    const nested = prune(children(block))
    if (nested.length > 0) return [{ ...block, children: nested } as Block]
    const index = state.blocks.indexOf(block)
    return index > first && index < last ? [structuredClone(block)] : []
  })
  // Text inside one table cell is inline text; only a table selection copies cells (returned above).
  const inline = [...selected.values()].map((block): Block => block.type === 'td' ? { id: '', type: 'p', children: block.children } : block)
  return { fragment: start.row === end.row && !anchorGap && !focusGap ? inline : prune(state.blocks), range: true }
}

/**
 * The node-selected blocks in document order, each run of selected list items inside a copy of its list (kind, start):
 * a bare item is no valid fragment at a text row or the root (nux/transfer/cut-paste-bullet-roundtrip).
 */
function selectedNodes(blocks: Blocks, ids: readonly string[]): Blocks {
  return blocks.flatMap((block): Blocks => {
    if (ids.includes(block.id)) return [block]
    if (block.type !== 'list') return selectedNodes(children(block), ids)
    const runs: Blocks = []
    let run: Blocks | undefined
    for (const item of block.children) {
      if (ids.includes(item.id)) {
        if (!run) { run = []; runs.push({ ...block, children: run }) }
        run.push(item)
      } else {
        run = undefined
        runs.push(...selectedNodes(children(item), ids))
      }
    }
    return runs
  })
}

/**
 * A copy at a caret copies the caret's block, a list item inside its list (Notion browser check 2026-10-30, D7 addendum:
 * copy-handler output: a copy at a caret in bullet "b" wrote `- b`); undefined for any other selection, in a table cell and in an empty row (Lexical: a
 * caret copies nothing there). A cut at a caret stays empty, so it removes nothing.
 */
function caretBlock(state: CoreState): { fragment: Blocks; range: boolean } | undefined {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table || gapOf(state.blocks, selection.anchor)) return undefined
  const entries = rows(state.blocks)
  const [anchor, focus] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
  if (anchor.row !== focus.row || anchor.offset !== focus.offset) return undefined
  const entry = entries[anchor.row]
  if (!entry || entry.block.type === 'td' || plain(entry.block) === '') return undefined
  const parent = entry.parents.at(-1)
  return { fragment: selectedNodes(state.blocks, [parent?.type === 'li' ? parent.id : entry.block.id]), range: false }
}

/**
 * A copied range's item whose own row is outside the range (it holds only its pruned sub-lists) copies no empty item:
 * its selected sub-items join the list at its depth. Notion browser check 2026-10-30, D7 addendum: copy-handler output
 * (a range from a nested "b" to a top-level "c" copies `- b\n- c`). Copy and cut only; a text drag keeps the pruned tree.
 */
function flatItems(blocks: Blocks): Blocks {
  return blocks.flatMap((block): Blocks => {
    const nested = flatItems(children(block))
    if (block.type === 'li' && nested.length > 0 && nested.every(child => child.type === 'list')) return nested.flatMap(list => children(list))
    return nested.length > 0 || children(block).length > 0 ? [{ ...block, children: nested } as Block] : [block]
  })
}

/**
 * The clipboard flavours of copying the selection: `text` its Markdown, `html` the Blocks carrier (lossless for every
 * block kind, read back by `pasteIntent`) followed by the fragment as HTML for other apps. A range inside one code row
 * copies that row's plain text only.
 */
export function copiedData(state: CoreState, op: 'copy' | 'cut' = 'copy', spaceId = ''): ClipboardData & { text: string } {
  const selection = state.selection
  if (selection && !selection.nodes?.length && !selection.table &&
    !gapOf(state.blocks, selection.anchor) && !gapOf(state.blocks, selection.focus)) {
    const entries = rows(state.blocks)
    const [start, end] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
      .toSorted((a, b) => a.row - b.row || a.offset - b.offset)
    const row = entries[start.row]
    if (start.row === end.row && row?.block.type === 'pre' &&
      (start.offset !== 0 || end.offset !== plain(row.block).length)) return { text: plain(row.block).slice(start.offset, end.offset) }
  }
  const copied = op === 'copy' ? caretBlock(state) ?? selectedFragment(state) : selectedFragment(state)
  const selected = copied.range ? { ...copied, fragment: flatItems(copied.fragment) } : copied
  const text = markdownOf(selected)
  // A selected subset of a question cannot move its source card.
  const parts = (value: unknown, counts = new Map<string, number>()): Map<string, number> => {
    if (!value || typeof value !== 'object') return counts
    if ('qid' in value && typeof value.qid === 'string') counts.set(value.qid, (counts.get(value.qid) ?? 0) + 1 + ('type' in value ? plain(value as Block).length : 0))
    Object.values(value).forEach(item => { parts(item, counts) })
    return counts
  }
  const full = parts(pageDocument(state.doc).content)
  if (op === 'cut' && [...parts(selected.fragment)].some(([qid, count]) => count < (full.get(qid) ?? 0))) op = 'copy'
  const marker = { op, origin: tabId, cutId: op === 'cut' ? ulid() : undefined, spaceId }
  return selected.fragment.length > 0 ? { text, html: carrierHtml(selected.fragment, marker) + blocksToHtml(selected.fragment) } : { text }
}

/**
 * The attribute of the empty span that carries a copied Blocks fragment inside text/html (donor: unni
 * `usePasteListener` `data-unni-state`). Native transport preserves the supplied HTML bytes.
 * Only this shared codec interprets the carrier.
 */
const CARRIER = 'data-easy-blocks'
const tabId = ulid()
const consumedCuts = new Set<string>()
export interface ClipboardMarker { op: 'copy' | 'cut'; origin: string; cutId?: string; spaceId: string }

/** Explicit same-tab cut command identity; never inferred from question content. */
export function clipboardCut(html: string | undefined, spaceId: string): string | undefined {
  const encoded = html && new RegExp(`${CARRIER}="([^"]*)"`, 'u').exec(html)?.[1]
  const marker = encoded ? decodedCarrier(encoded) : undefined
  if (questionIds(carriedBlocks(html) ?? []).size === 0) return undefined
  return record(marker) && marker.op === 'cut' && marker.origin === tabId && marker.spaceId === spaceId && typeof marker.cutId === 'string' && !consumedCuts.has(marker.cutId) ? marker.cutId : undefined
}

/** The empty span carrying `fragment`; a view that renders its own copied HTML prepends it. */
export function carrierHtml(fragment: Blocks, marker?: ClipboardMarker): string {
  return `<span ${CARRIER}="${encodeURIComponent(JSON.stringify(marker ? { fragment, ...marker } : fragment))}"></span>`
}

/** The Blocks fragment a carrier span in `html` holds; undefined without one or when URI, JSON or Blocks validation fails. */
export function carriedBlocks(html: string | undefined): Blocks | undefined {
  const encoded = html && new RegExp(`${CARRIER}="([^"]*)"`, 'u').exec(html)?.[1]
  if (!encoded) return undefined
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encoded))
    const fragment = record(parsed) ? parsed.fragment : parsed
    return validBlocks(fragment) ? safeLinks(fragment) : undefined
  } catch {
    return undefined
  }
}

/**
 * The paste entry point's flavours of a clipboard or drop transfer: text/markdown or text/plain as `text`, text/html,
 * the Lexical view's JSON and the transferred files (donor: unni `usePasteListener`). A view uploads `files` itself.
 */
export function clipboardData(transfer: DataTransfer): ClipboardData {
  const get = (type: string) => transfer.getData(type) || undefined
  // A URI list alone (a dragged link) pastes its URIs, comment lines dropped (Lexical clipboard text/uri-list fallback).
  const uris = get('text/uri-list')?.split(/\r?\n/u).filter((line) => line && !line.startsWith('#')) ?? []
  return {
    text: get('text/markdown') ?? get('text/plain') ?? (uris.length > 0 ? uris.join(' ') : undefined),
    html: get('text/html'),
    lexical: get(LEXICAL_MIME),
    files: Array.from(transfer.files),
  }
}

export const LEXICAL_MIME = 'application/x-lexical-editor'

/** The paste intent of clipboard flavours: the HTML's Blocks carrier, then the Lexical view's JSON, then HTML, then Markdown text. */
export function pasteIntent(data: ClipboardData, own: ReadonlySet<string> | (() => ReadonlySet<string>) = new Set(), spaceId = ''): Extract<EditorIntent, { type: 'insertFromPaste' }> {
  const fragment = carriedBlocks(data.html) ?? foreignLexicalBlocks(data.lexical)
  const intent: Extract<EditorIntent, { type: 'insertFromPaste' }> = fragment
    ? { type: 'insertFromPaste', md: data.text ?? '', fragment }
    : data.html === undefined ? { type: 'insertFromPaste', md: data.text ?? '' }
    : { type: 'insertFromPaste', html: data.html, ...(data.text === undefined ? {} : { text: data.text }) }
  const encoded = data.html && new RegExp(`${CARRIER}="([^"]*)"`, 'u').exec(data.html)?.[1]
  const marker: unknown = encoded ? decodedCarrier(encoded) : undefined
  let copy = false
  if (fragment && record(marker) && (marker.op === 'copy' || marker.op === 'cut')) {
    const localCut = marker.op === 'cut' && marker.origin === tabId && typeof marker.cutId === 'string'
    const firstCut = localCut && marker.spaceId === spaceId && !consumedCuts.has(marker.cutId as string)
    copy = !firstCut
    if (localCut) consumedCuts.add(marker.cutId as string)
  }
  // INV-L9: every copy gets fresh IDs; every paste remaps IDs already owned by this document.
  const blocks = pasteBlocks(intent)
  const questions = questionIds(blocks)
  const remap = copy ? questions : questions.size === 0 ? questions : typeof own === 'function' ? own() : own
  // HTML is parsed once: the blocks parsed here travel as the fragment (md '' keeps the URL-link branch off, as for HTML;
  // `text` keeps the plain flavour a code block takes).
  if (![...questions].some(qid => remap.has(qid))) return intent.html === undefined ? intent : { type: 'insertFromPaste', md: '', fragment: blocks, ...(intent.text === undefined ? {} : { text: intent.text }) }
  const qids = new Map<string, string>()
  return { type: 'insertFromPaste', md: data.text ?? '', fragment: blocks.map(block => unsavedCopy(block, qids, remap)) }
}

/** Foreign serialized custom payloads cross the same Blocks boundary as HTML carriers. */
function foreignLexicalBlocks(serialized: string | undefined): Blocks | undefined {
  if (serialized === undefined) return undefined
  try {
    const fragment = lexicalClipboardBlocks(parseJson(serialized))
    return validBlocks(fragment) ? safeLinks(fragment) : undefined
  } catch {
    return undefined
  }
}

function decodedCarrier(encoded: string): unknown {
  try { return JSON.parse(decodeURIComponent(encoded)) } catch { return undefined }
}

function parseJson(json: string): unknown {
  try {
    return JSON.parse(json)
  } catch {
    return undefined
  }
}

/** External links cross the same href gate as typed ones: an unsafe link (`javascript:`, `data:`…) is dropped, its text kept. */
function safeLinks(blocks: Blocks): Blocks {
  return blocks.flatMap((block): Blocks => {
    const next: Record<string, unknown> = { ...block }
    for (const field of ['children', 'caption', 'question', 'explanation', 'author']) {
      if (Array.isArray(next[field])) next[field] = safeLinks(next[field] as Blocks)
    }
    if (Array.isArray(next.opts)) next.opts = (next.opts as Blocks[]).map(group => safeLinks(group))
    if (block.type === 'a' && safeLinkHref(block.href) === null) return next.children as Blocks
    return [next as unknown as Block]
  })
}

/** Validate external tree shape before the paste reducer can traverse it. */
function validBlocks(value: unknown): value is Blocks {
  return Array.isArray(value) && value.every(block => validBlock(block))
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function validBlock(block: unknown): boolean {
  if (!record(block)) return false
  if (typeof block.id !== 'string' || typeof block.type !== 'string') return false
  for (const field of ['children', 'caption', 'question', 'explanation', 'author']) {
    if (block[field] !== undefined && !validBlocks(block[field])) return false
  }
  const strings = (...fields: string[]) => fields.every(field => typeof block[field] === 'string')
  switch (block.type) {
    case 'text': return strings('text')
    case 'chars': return Array.isArray(block.text) && block.text.every((segment: unknown) =>
      record(segment) && typeof segment.id === 'string' && typeof segment.text === 'string')
    case 'p': case 'quote': case 'list': case 'li': case 'table': case 'tr': case 'td':
    case 'pre': case 'cols': case 'root': return validBlocks(block.children)
    case 'h': return [1, 2, 3].includes(Number(block.level)) && validBlocks(block.children)
    // Markdown columns serialize percentage widths as strings (parser.test.ts).
    case 'col': return (typeof block.width === 'number' || typeof block.width === 'string') && validBlocks(block.children)
    case 'code': return block.latex === true && validBlocks(block.children)
    case 'em': return strings('color') && validBlocks(block.children)
    case 'l': return strings('lang') && validBlocks(block.children)
    case 'a': return strings('href') && validBlocks(block.children)
    case 'cloze': return strings('qid', 'fractionalIndex') && validBlocks(block.children)
    case 'comment': return strings('commentId') && validBlocks(block.children)
    case 'criticComment': return strings('body')
    case 'comments': return Array.isArray(block.threads) && block.threads.every((thread: unknown) =>
      record(thread) && typeof thread.threadId === 'string' &&
      ['open', 'resolved'].includes(String(thread.status)) && Array.isArray(thread.comments) &&
      thread.comments.every((message: unknown) => record(message) && typeof message.id === 'string' &&
        typeof message.authorId === 'string' && typeof message.createdAt === 'number' && typeof message.body === 'string'))
    case 'diff': return ['add', 'delete', 'context'].includes(String(block.kind)) && validBlocks(block.children)
    case 'review': return ['add', 'delete', 'update'].includes(String(block.change)) &&
      ['suggested', 'approved'].includes(String(block.state)) && ['inline', 'block'].includes(String(block.display)) && validBlocks(block.children)
    case 'definition': return ['link', 'footnote'].includes(String(block.kind)) && validBlocks(block.children)
    case 'alert': return strings('icon', 'color') && validBlocks(block.children)
    case 'questionGroup': return validBlocks(block.children)
    case 'rule': return strings('key', 'language', 'scope') && Array.isArray(block.predicts) && block.predicts.every(item => typeof item === 'string') && validBlocks(block.children)
    case 'br': case 'hr': return true
    case 'pageTitle': case 'pageMeta': return strings('title')
    case 'img': case 'video': case 'audio': return strings('src')
    case 'preview': return strings('src') && block.subtype === 'youtube'
    case 'file': return strings('src', 'name') && typeof block.size === 'number'
    case 'page': case 'pageLink': return strings('pageId')
    case 'pages': return Array.isArray(block.ids) && block.ids.every(id => typeof id === 'string')
    case 'mention': return block.subtype === 'date' ? typeof block.date === 'number'
      : ['user', 'page'].includes(String(block.subtype)) && strings('uid')
    case 'question': {
      if (!strings('qid', 'fractionalIndex')) return false
      if (block.subtype === 'sa') return Array.isArray(block.answer) && block.answer.every(answer => typeof answer === 'string')
      if (block.subtype === 'la') return Array.isArray(block.criteria) && block.criteria.every(item => typeof item === 'string')
      if (block.subtype === 'sc' || block.subtype === 'mc') return Array.isArray(block.opts) && block.opts.every(validBlocks) &&
        (block.subtype === 'sc' ? typeof block.answer === 'number' : Array.isArray(block.answer) && block.answer.every(answer => typeof answer === 'number'))
      if (block.subtype === 'sr') { const answer = block.answer as Record<string, unknown> | undefined; return !!answer && typeof answer.src === 'string' && typeof answer.transcript === 'string' }
      return false
    }
    default: return false
  }
}
